import React, { useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { CheckCircle, Storefront, Close } from "@mui/icons-material";
import {
  handleCompanySwitchAsync,
  useAuth,
} from "../../../contexts/AuthProvider";
import { useBillingCompanyContext } from "../../../providers";
import { saveBillingCompanyInStorage } from "../../../providers/BillingCompanyProvider/BillingCompanyProvider.storage";
import { useDispatchWrapper } from "../../../hooks";
import { showMessage } from "../../../redux/messages/messagesActions";
import { config } from "../../../config";
import { ICompanyOutlet } from "../../../redux/companyOutlet/companyOutlet.types";

export interface MobileCompanySheetProps {
  open: boolean;
  onClose: () => void;
}

export const MobileCompanySheet: React.FC<MobileCompanySheetProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const {
    user: { affilated_company_outlet_uuids },
  } = useAuth();
  const userBillingInfo = useBillingCompanyContext();
  const dispatch = useDispatchWrapper();

  const [isLoading, setIsLoading] = useState(false);
  const [switchingUuid, setSwitchingUuid] = useState<string | null>(null);

  const currentOutletName = userBillingInfo.comapny.company_outlet_name;
  const currentOutletUuid = userBillingInfo.comapny.company_outlet_uuid;

  const handleSwitchOutlet = async (outlet: ICompanyOutlet) => {
    if (outlet.company_outlet_uuid === currentOutletUuid || isLoading) return;

    try {
      setIsLoading(true);
      setSwitchingUuid(outlet.company_outlet_uuid);

      await new Promise<void>((resolve, reject) => {
        handleCompanySwitchAsync(
          outlet.company_outlet_uuid,
          (isSuccess) => {
            if (isSuccess) {
              resolve();
            } else {
              reject(new Error("Failed to switch outlet"));
            }
          },
          (error) => {
            reject(new Error(error));
          },
        );
      });

      // Persist to storage
      saveBillingCompanyInStorage(outlet);

      dispatch(
        showMessage({
          type: "success",
          message: `Switched to ${outlet.company_outlet_name}`,
          displayAs: "snackbar",
        }),
      );

      onClose();
      // Reload to invalidate all cached data and refetch with new outlet context
      window.history.replaceState(null, "", "/dashboard");
      window.location.href = "/dashboard";
    } catch (error: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            error.message || "Failed to switch outlet. Please try again.",
          displayAs: "snackbar",
        }),
      );
    } finally {
      setIsLoading(false);
      setSwitchingUuid(null);
    }
  };

  const outlets = (affilated_company_outlet_uuids || []).filter(
    (outlet) => outlet && outlet.company_outlet_uuid,
  );

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={isLoading ? undefined : onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: "24px",
          borderTopRightRadius: "24px",
          maxHeight: "85vh",
          backgroundColor: isDark ? "#1e293b" : "#ffffff",
          backgroundImage: "none",
          p: 0,
          pb: "calc(16px + env(safe-area-inset-bottom, 0px))",
        },
      }}
    >
      {/* Pull indicator */}
      <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5, pb: 1 }}>
        <Box
          sx={{
            width: 40,
            height: 4,
            borderRadius: 2,
            backgroundColor: isDark ? "rgba(255,255,255,0.2)" : "#cbd5e1",
          }}
        />
      </Box>

      {/* Header */}
      <Box sx={{ px: 3, pt: 1, pb: 1.5 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, fontSize: "1.25rem" }}
            >
              Select Company / Outlet
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Switching outlet will update all reports and leads
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={onClose}
            disabled={isLoading}
            sx={{
              minWidth: "auto",
              p: 0.5,
              borderRadius: "50%",
              color: "text.secondary",
            }}
          >
            <Close fontSize="small" />
          </Button>
        </Stack>
      </Box>

      <Divider />

      {/* Outlets List */}
      <List sx={{ px: 2, py: 1.5, maxHeight: "55vh", overflowY: "auto" }}>
        {outlets.map((outlet) => {
          const isCurrent = outlet.company_outlet_uuid === currentOutletUuid;
          const isSwitching = switchingUuid === outlet.company_outlet_uuid;

          return (
            <ListItemButton
              key={outlet.company_outlet_uuid}
              onClick={() => handleSwitchOutlet(outlet)}
              disabled={isLoading || isCurrent}
              sx={{
                borderRadius: "16px",
                my: 0.75,
                p: 1.5,
                backgroundColor: isCurrent
                  ? isDark
                    ? "rgba(26, 151, 245, 0.15)"
                    : "rgba(26, 151, 245, 0.08)"
                  : "transparent",
                border: `1px solid ${
                  isCurrent
                    ? theme.palette.primary.main
                    : isDark
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,0,0,0.04)"
                }`,
                "&:hover": {
                  backgroundColor: isCurrent
                    ? isDark
                      ? "rgba(26, 151, 245, 0.2)"
                      : "rgba(26, 151, 245, 0.12)"
                    : isDark
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(0,0,0,0.03)",
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 46 }}>
                <Avatar
                  src={
                    outlet.logo
                      ? outlet.logo.startsWith("http")
                        ? outlet.logo
                        : `${config.assets.s3BaseUrl}/${outlet.logo}`
                      : undefined
                  }
                  sx={{
                    width: 38,
                    height: 38,
                    bgcolor: isCurrent
                      ? "primary.main"
                      : isDark
                        ? "grey.700"
                        : "grey.200",
                    color: isCurrent ? "#fff" : isDark ? "#fff" : "grey.700",
                    fontWeight: 700,
                    fontSize: "0.95rem",
                  }}
                >
                  {outlet.company_outlet_name?.charAt(0)?.toUpperCase() || (
                    <Storefront fontSize="small" />
                  )}
                </Avatar>
              </ListItemIcon>

              <ListItemText
                primary={outlet.company_outlet_name}
                secondary={
                  isCurrent ? "Current active outlet" : "Tap to switch"
                }
                primaryTypographyProps={{
                  fontWeight: isCurrent ? 700 : 500,
                  fontSize: "0.95rem",
                  color: isCurrent ? "primary.main" : "text.primary",
                }}
                secondaryTypographyProps={{
                  fontSize: "0.75rem",
                  color: isCurrent ? "primary.main" : "text.secondary",
                  fontWeight: isCurrent ? 600 : 400,
                }}
              />

              {isCurrent && (
                <CheckCircle
                  color="primary"
                  sx={{ fontSize: "1.25rem", ml: 1 }}
                />
              )}

              {isSwitching && <CircularProgress size={20} sx={{ ml: 1 }} />}
            </ListItemButton>
          );
        })}
      </List>
    </Drawer>
  );
};
